import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import appointmentService from '../services/AppointmentService';
import customerService from '../services/CustomerService';
import practitionerService from '../services/PractitionerService';
import treatmentService from '../services/TreatmentService';
import venueService from '../services/VenueService';
import workOrderService from '../services/WorkOrderService';
import treatmentRecordService from '../services/TreatmentRecordService';
import AppointmentList from '../components/appointments/AppointmentList';
import AppointmentFilters from '../components/appointments/AppointmentFilters';
import AppointmentForm from '../components/appointments/AppointmentForm';
import AppointmentCalendar from '../components/appointments/AppointmentCalendar';
import {
  Card,
  Button,
  SearchInput,
  Select,
  Modal,
  Alert
} from '../components/ui';
import { appointmentStatusLabels } from '../constants/statusLabels';

/**
 * AppointmentsPage component for managing appointments
 * @returns {React.ReactElement} AppointmentsPage component
 */
const AppointmentsPage = () => {
  // Navigation
  const navigate = useNavigate();

  // Auth context
  const { hasPermission } = useAuth();

  // State for appointments data
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for reference data
  const [customers, setCustomers] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [venues, setVenues] = useState([]);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [statusFilter, setStatusFilter] = useState('upcoming');
  const [practitionerFilter, setPractitionerFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');

  // State for create/edit modal
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for filter visibility
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const canManageAppointments = hasPermission('appointments', 'write');

  // Fetch appointments and reference data on component mount
  useEffect(() => {
    fetchAppointmentsAndReferenceData();
  }, []);

  // Function to fetch appointments and reference data
  const fetchAppointmentsAndReferenceData = async () => {
    setLoading(true);
    try {
      // Fetch appointments
      const appointmentsData = appointmentService.getAllAppointments();

      // Fetch customers for name display
      const customersData = customerService.getAllCustomers();
      setCustomers(customersData);

      // Fetch practitioners for name display
      const practitionersData = practitionerService.getAllPractitioners();
      setPractitioners(practitionersData);

      // Fetch treatments for name display
      const treatmentsData = treatmentService.getAllTreatments();
      setTreatments(treatmentsData);

      // Fetch venues for name display
      const venuesData = venueService.getAllVenues();
      setVenues(venuesData);

      // Enhance appointments with names and work order status
      const enhancedAppointments = appointmentsData.map(appointment => {
        const customer = customersData.find(c => c.id === appointment.customerId);
        const practitioner = practitionersData.find(p => p.id === appointment.practitionerId);
        const treatment = treatmentsData.find(t => t.id === appointment.treatmentId);
        const venue = venuesData.find(v => v.id === appointment.venueId);

        // Calculate duration if not present
        let duration = appointment.duration;
        if (!duration && appointment.startTime && appointment.endTime) {
          const start = new Date(appointment.startTime);
          const end = new Date(appointment.endTime);
          duration = Math.round((end - start) / 60000); // Convert ms to minutes
        }

        // Work order status
        const workOrders = workOrderService.getWorkOrdersByAppointmentId(appointment.id);
        let workOrderStatus = '无工单';
        if (workOrders && workOrders.length > 0) {
          if (workOrders.every(o => o.status === 'completed')) {
            workOrderStatus = '全部完成';
          } else if (workOrders.some(o => o.status === 'in_progress')) {
            workOrderStatus = '进行中';
          } else {
            workOrderStatus = '待调理';
          }
        }

        // 获取调理记录
        const treatmentRecords = treatmentRecordService.getTreatmentRecordsByAppointment(appointment.id) || [];

        return {
          ...appointment,
          // Map startTime to appointmentTime for display compatibility
          appointmentTime: appointment.startTime || appointment.appointmentTime,
          duration: duration || (treatment?.duration || 60), // Fallback to treatment duration or 60 minutes
          customerName: customer?.name || '未知客户',
          customerPhone: customer?.phoneNumber || '',
          practitionerName: practitioner?.name || '未指定',
          treatmentName: treatment?.name || '未指定项目',
          venueName: venue?.name || '未指定',
          workOrderStatus,
          treatmentRecords
        };
      });

      setAppointments(enhancedAppointments);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('获取预约数据失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter appointments based on search and filters
  const filteredAppointments = appointments.filter(appointment => {
    // Enhanced search - search by customer info, practitioner, venue, treatment, time, and notes
    const searchLower = searchTerm.toLowerCase();

    // Find related entities for comprehensive search
    const practitioner = practitioners.find(p => p.id === appointment.practitionerId);
    const venue = venues.find(v => v.id === appointment.venueId);
    const customer = customers.find(c => c.id === appointment.customerId);

    // Format appointment time for search
    const appointmentTimeFormatted = appointment.appointmentTime
      ? new Date(appointment.appointmentTime).toLocaleDateString('zh-CN')
      : '';

    const matchesSearch = searchTerm === '' ||
      // Customer information
      customer?.name?.toLowerCase().includes(searchLower) ||
      customer?.phoneNumber?.includes(searchTerm) ||
      appointment.customerName?.toLowerCase().includes(searchLower) ||
      appointment.customerPhone?.includes(searchTerm) ||

      // Practitioner information
      practitioner?.name?.toLowerCase().includes(searchLower) ||
      appointment.practitionerName?.toLowerCase().includes(searchLower) ||

      // Venue information
      venue?.name?.toLowerCase().includes(searchLower) ||
      venue?.location?.toLowerCase().includes(searchLower) ||
      appointment.venueName?.toLowerCase().includes(searchLower) ||

      // Treatment information
      appointment.treatmentName?.toLowerCase().includes(searchLower) ||

      // Time information
      appointmentTimeFormatted.includes(searchTerm) ||

      // Status information
      appointment.status?.toLowerCase().includes(searchLower) ||

      // Notes
      appointment.notes?.toLowerCase().includes(searchLower);

    // Filter by date range
    const appointmentDate = new Date(appointment.appointmentTime);
    const matchesDateRange =
      (!dateRange.startDate || appointmentDate >= new Date(dateRange.startDate)) &&
      (!dateRange.endDate || appointmentDate <= new Date(dateRange.endDate));

    // Filter by status
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      if (statusFilter === 'upcoming') {
        matchesStatus = ['scheduled', 'confirmed'].includes(appointment.status) &&
          appointmentDate >= new Date();
      } else {
        matchesStatus = appointment.status === statusFilter;
      }
    }

    // Filter by practitioner
    const matchesPractitioner = practitionerFilter === 'all' ||
      appointment.practitionerId === practitionerFilter;

    // Filter by venue  
    const matchesVenue = venueFilter === 'all' ||
      appointment.venueId === venueFilter;

    return matchesSearch &&
      matchesDateRange &&
      matchesStatus &&
      matchesPractitioner &&
      matchesVenue;
  });

  // Handle opening the appointment form for creating
  const handleCreateAppointment = (date = null) => {
    setSelectedAppointment(null);

    // If a date is provided (from calendar), prepare it
    let initialDate = null;
    if (date) {
      // Set the time to the current hour, rounded to the nearest hour
      const currentTime = new Date();
      date.setHours(currentTime.getHours());
      date.setMinutes(0);
      date.setSeconds(0);
      initialDate = date;
    }

    setShowAppointmentModal(true);
  };

  // Handle opening the appointment form for editing
  const handleEditAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  // Handle appointment deletion
  const handleDeleteAppointment = (appointmentId) => {
    if (window.confirm('确定要删除此预约吗？此操作无法撤销。')) {
      try {
        appointmentService.deleteAppointment(appointmentId);
        fetchAppointmentsAndReferenceData();
      } catch (err) {
        console.error('Error deleting appointment:', err);
        setError('删除预约失败');
      }
    }
  };

  // Handle appointment status change
  const handleStatusChange = (appointmentId, newStatus) => {
    try {
      appointmentService.updateAppointmentStatus(appointmentId, newStatus);
      fetchAppointmentsAndReferenceData();
    } catch (err) {
      console.error('Error updating appointment status:', err);
      setError('更新预约状态失败');
    }
  };

  // Handle appointment form submission
  const handleSubmitAppointment = async (formData) => {
    setIsSubmitting(true);

    try {
      if (selectedAppointment) {
        // Update existing appointment
        appointmentService.updateAppointment(selectedAppointment.id, formData);
      } else {
        // Create new appointment
        appointmentService.createAppointment(formData);
      }

      // Refresh appointment list
      fetchAppointmentsAndReferenceData();
      setShowAppointmentModal(false);
    } catch (err) {
      console.error('Error saving appointment:', err);
      setError('保存预约失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle view specific appointment
  const handleViewAppointment = (appointmentId) => {
    navigate(`/appointments/${appointmentId}`);
  };

  // Close the appointment modal
  const handleCancelModal = () => {
    setShowAppointmentModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              预约管理
            </h1>
            <p className="text-gray-600">
              管理预约信息，查看预约状态和时间安排
            </p>
          </div>
          {canManageAppointments && (
            <div className="mt-4 sm:mt-0">
              <Button onClick={() => handleCreateAppointment()}>
                <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                新建预约
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
          {/* 搜索和视图切换始终展示 */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
            <div className="flex-1 max-w-2xl">
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="搜索客户姓名、电话、备注、调理师、场地..."
                className="w-full"
              />
            </div>
            <div className="flex bg-gray-100 rounded-xl p-1" role="group">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${viewMode === 'list'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                列表视图
              </button>
              <button
                type="button"
                onClick={() => setViewMode('calendar')}
                className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${viewMode === 'calendar'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                日历视图
              </button>
            </div>
          </div>
          {/* 筛选条件折叠区 */}
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-lg">筛选条件</span>
            <Button
              variant="ghost"
              onClick={() => setShowFilters(v => !v)}
              className="text-emerald-600"
            >
              {showFilters ? '收起' : '展开'}
            </Button>
          </div>
          {showFilters && (
            <>
              <AppointmentFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dateRange={dateRange}
                setDateRange={setDateRange}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                practitionerFilter={practitionerFilter}
                setPractitionerFilter={setPractitionerFilter}
                practitioners={practitioners}
                venueFilter={venueFilter}
                setVenueFilter={setVenueFilter}
                venues={venues}
              />
              {/* 结果统计和筛选标签 */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">共找到 {filteredAppointments.length} 条预约记录</span>
                    {searchTerm && (
                      <span className="ml-4 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md text-xs">
                        搜索: "{searchTerm}"
                      </span>
                    )}
                  </div>
                  {/* Active Filter Indicators */}
                  <div className="flex items-center gap-2 text-xs">
                    {statusFilter !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md">
                        状态: {statusFilter === 'upcoming' ? '即将到来' : (appointmentStatusLabels[statusFilter] || statusFilter)}
                      </span>
                    )}
                    {practitionerFilter !== 'all' && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md">
                        调理师: {practitioners.find(p => p.id === practitionerFilter)?.name || practitionerFilter}
                      </span>
                    )}
                    {venueFilter !== 'all' && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-md">
                        场地: {venues.find(v => v.id === venueFilter)?.name || venueFilter}
                      </span>
                    )}
                    {(dateRange.startDate || dateRange.endDate) && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md">
                        时间筛选
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Error Display */}
        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content Area */}
        {loading ? (
          <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <p className="text-gray-600">加载预约数据...</p>
            </div>
          </Card>
        ) : (
          <>
            {/* List View */}
            {viewMode === 'list' && (
              <>
                {filteredAppointments.length > 0 ? (
                  <AppointmentList
                    appointments={filteredAppointments}
                    onEdit={handleEditAppointment}
                    onDelete={handleDeleteAppointment}
                    onStatusChange={handleStatusChange}
                    canManageAppointments={canManageAppointments}
                  />
                ) : (
                  <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
                    <div className="text-center py-16">
                      <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {searchTerm ? '没有找到匹配的预约' : '还没有预约'}
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {searchTerm ? '尝试使用不同的搜索词' : '开始创建预约来管理您的日程'}
                      </p>
                      {canManageAppointments && !searchTerm && (
                        <Button onClick={() => handleCreateAppointment()}>
                          添加第一个预约
                        </Button>
                      )}
                    </div>
                  </Card>
                )}
              </>
            )}

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <Card variant="simple" className="border-emerald-200/50 bg-white/70 backdrop-blur-sm">
                <AppointmentCalendar
                  appointments={filteredAppointments}
                  practitioners={practitioners}
                  onViewAppointment={handleViewAppointment}
                  onCreateAppointment={handleCreateAppointment}
                  canManageAppointments={canManageAppointments}
                />
              </Card>
            )}
          </>
        )}

        {/* Appointment Form Modal */}
        <Modal
          isOpen={showAppointmentModal}
          onClose={handleCancelModal}
          title={selectedAppointment ? '编辑预约' : '创建新预约'}
          size="xlarge"
          className="max-w-4xl"
        >
          <AppointmentForm
            appointment={selectedAppointment}
            onSubmit={handleSubmitAppointment}
            onCancel={handleCancelModal}
            isLoading={isSubmitting}
            customers={customers}
            preSelectedCustomerId={null}
          />
        </Modal>
      </div>
    </div>
  );
};

export default AppointmentsPage;